import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeDetail from "@/pages/EmployeeDetail";
import ExitChecklist from "@/pages/ExitChecklist";
import Attendance from "@/pages/Attendance";
import CheckIn from "@/pages/CheckIn";
import Leaves from "@/pages/Leaves";
import Holidays from "@/pages/Holidays";
import Profile from "@/pages/Profile";
import AgencyTenants from "@/pages/AgencyTenants";
import Clients from "@/pages/Clients";
import Projects from "@/pages/Projects";
import Allocations from "@/pages/Allocations";
import Bench from "@/pages/Bench";
import Timesheets from "@/pages/Timesheets";
import TimesheetApprovals from "@/pages/TimesheetApprovals";
import Compliance from "@/pages/Compliance";
import Payroll from "@/pages/Payroll";
import Payslips from "@/pages/Payslips";
import Statutory from "@/pages/Statutory";
import InvoiceData from "@/pages/InvoiceData";
import Recruitment from "@/pages/Recruitment";
import Performance from "@/pages/Performance";
import Regularization from "@/pages/Regularization";
import Shifts from "@/pages/Shifts";
import Letters from "@/pages/Letters";
import BulkLetters from "@/pages/BulkLetters";
import IncrementCycles from "@/pages/IncrementCycles";
import LetterStudio from "@/pages/LetterStudio";
import Connectors from "@/pages/Connectors";
import Reimbursements from "@/pages/Reimbursements";
import CompanyHub from "@/pages/CompanyHub";
import PerformanceAdvanced from "@/pages/PerformanceAdvanced";
import PayrollAdmin from "@/pages/PayrollAdmin";
import PayrollRegister from "@/pages/PayrollRegister";
import OrgChart from "@/pages/OrgChart";
import MyTeam from "@/pages/MyTeam";
import AuditLog from "@/pages/AuditLog";
import ComplianceCalendar from "@/pages/ComplianceCalendar";
import Surveys from "@/pages/Surveys";
import Onboarding from "@/pages/Onboarding";
import Operations from "@/pages/Operations";
import ApprovalsInbox from "@/pages/ApprovalsInbox";
import Kudos from "@/pages/Kudos";
import OKR from "@/pages/OKR";
import Rewards from "@/pages/Rewards";
import Announcements from "@/pages/Announcements";
import Reports from "@/pages/Reports";
import Referrals from "@/pages/Referrals";
import ExitDashboard from "@/pages/ExitDashboard";
import Help from "@/pages/Help";
import AdminPanel from "@/pages/AdminPanel";
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import Billing from "@/pages/Billing";
import Billability from "@/pages/Billability";
import InvoicingPipeline from "@/pages/InvoicingPipeline";
import StatCompliance from "@/pages/StatCompliance";
import Tasks from "@/pages/Tasks";
import Trips from "@/pages/Trips";
import TripReview from "@/pages/TripReview";
import ReimbursementPolicies from "@/pages/ReimbursementPolicies";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Terms from "@/pages/Terms";
import DeleteAccount from "@/pages/DeleteAccount";
import ResetPassword from "@/pages/ResetPassword";
import AccessControl from "@/pages/AccessControl";
import SalesConsole from "@/pages/SalesConsole";
import Learning from "@/pages/Learning";
import ATSPipeline from "@/pages/ATSPipeline";
import AIGenerator from "@/pages/AIGenerator";
import KPIDashboard from "@/pages/KPIDashboard";
import TaxDashboard from "@/pages/TaxDashboard";
import { CareersLanding, CareersApply, AcceptOffer } from "@/pages/Careers";
import TenantGuard from "@/components/TenantGuard";
import { Toaster } from "@/components/ui/sonner";

const ADMIN = ["super_admin", "company_admin", "hr"];
const HR_MGR = ["super_admin", "company_admin", "hr", "manager"];
const ALL = ["super_admin", "company_admin", "hr", "manager", "employee"];
const EMP = ["employee", "hr", "manager"];

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center bg-zinc-50"><div className="text-sm text-zinc-500">Loading your workspace…</div></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

function RoleGate({ roles, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/careers" element={<CareersLanding />} />
            <Route path="/careers/apply/:jobId" element={<CareersApply />} />
            <Route path="/careers/accept-offer/:token" element={<AcceptOffer />} />
            <Route path="/help" element={<Help />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="employees" element={<RoleGate roles={HR_MGR}><Employees /></RoleGate>} />
              <Route path="employees/:id" element={<RoleGate roles={HR_MGR}><EmployeeDetail /></RoleGate>} />
              <Route path="employees/:id/exit" element={<RoleGate roles={ADMIN}><ExitChecklist /></RoleGate>} />
              <Route path="bench" element={<RoleGate roles={HR_MGR}><Bench /></RoleGate>} />
              <Route path="clients" element={<RoleGate roles={HR_MGR}><Clients /></RoleGate>} />
              <Route path="projects" element={<RoleGate roles={HR_MGR}><Projects /></RoleGate>} />
              <Route path="allocations" element={<RoleGate roles={HR_MGR}><Allocations /></RoleGate>} />
              <Route path="attendance" element={<RoleGate roles={HR_MGR}><Attendance /></RoleGate>} />
              <Route path="checkin" element={<RoleGate roles={EMP}><CheckIn /></RoleGate>} />
              <Route path="timesheets" element={<RoleGate roles={EMP}><Timesheets /></RoleGate>} />
              <Route path="timesheet-approvals" element={<RoleGate roles={HR_MGR}><TimesheetApprovals /></RoleGate>} />
              <Route path="compliance" element={<Compliance />} />
              <Route path="leaves" element={<Leaves />} />
              <Route path="holidays" element={<Holidays />} />
              <Route path="payroll" element={<RoleGate roles={ADMIN}><Payroll /></RoleGate>} />
              <Route path="payslips" element={<RoleGate roles={EMP}><Payslips /></RoleGate>} />
              <Route path="statutory" element={<RoleGate roles={ADMIN}><Statutory /></RoleGate>} />
              <Route path="invoicing" element={<RoleGate roles={HR_MGR}><InvoiceData /></RoleGate>} />
              <Route path="recruitment" element={<RoleGate roles={HR_MGR}><Recruitment /></RoleGate>} />
              <Route path="performance" element={<Performance />} />
              <Route path="regularization" element={<Regularization />} />
              <Route path="shifts" element={<RoleGate roles={HR_MGR}><Shifts /></RoleGate>} />
              <Route path="letters" element={<Letters />} />
              <Route path="bulk-letters" element={<RoleGate roles={HR_MGR}><BulkLetters /></RoleGate>} />
              <Route path="letter-studio" element={<RoleGate roles={ADMIN}><LetterStudio /></RoleGate>} />
              <Route path="increment-cycles" element={<RoleGate roles={HR_MGR}><IncrementCycles /></RoleGate>} />
              <Route path="increment-cycles/:id" element={<RoleGate roles={HR_MGR}><IncrementCycles /></RoleGate>} />
              <Route path="policies" element={<Navigate to="/app/admin" replace />} />
              <Route path="connectors" element={<RoleGate roles={ADMIN}><Connectors /></RoleGate>} />
              <Route path="reimbursements" element={<Reimbursements />} />
              <Route path="company" element={<CompanyHub />} />
              <Route path="org-chart" element={<TenantGuard><OrgChart /></TenantGuard>} />
              <Route path="my-team" element={<TenantGuard><MyTeam /></TenantGuard>} />
              <Route path="audit-log" element={<RoleGate roles={ADMIN}><AuditLog /></RoleGate>} />
              <Route path="compliance-calendar" element={<RoleGate roles={ADMIN}><ComplianceCalendar /></RoleGate>} />
              <Route path="payroll-register" element={<RoleGate roles={ADMIN}><PayrollRegister /></RoleGate>} />
              <Route path="performance-advanced" element={<TenantGuard><PerformanceAdvanced /></TenantGuard>} />
              <Route path="payroll-admin" element={<RoleGate roles={ADMIN}><TenantGuard><PayrollAdmin /></TenantGuard></RoleGate>} />
              <Route path="tax-dashboard" element={<RoleGate roles={ADMIN}><TenantGuard><TaxDashboard /></TenantGuard></RoleGate>} />
              <Route path="surveys" element={<TenantGuard><Surveys /></TenantGuard>} />
              <Route path="onboarding" element={<TenantGuard><Onboarding /></TenantGuard>} />
              <Route path="operations" element={<TenantGuard><Operations /></TenantGuard>} />
              <Route path="approvals-inbox" element={<TenantGuard><ApprovalsInbox /></TenantGuard>} />
              <Route path="kudos" element={<TenantGuard><Kudos /></TenantGuard>} />
              <Route path="okr" element={<TenantGuard><OKR /></TenantGuard>} />
              <Route path="rewards" element={<TenantGuard><Rewards /></TenantGuard>} />
              <Route path="announcements" element={<TenantGuard><Announcements /></TenantGuard>} />
              <Route path="reports" element={<TenantGuard><Reports /></TenantGuard>} />
              <Route path="referrals" element={<TenantGuard><Referrals /></TenantGuard>} />
              <Route path="exit-dashboard" element={<RoleGate roles={ADMIN}><TenantGuard><ExitDashboard /></TenantGuard></RoleGate>} />
              <Route path="help" element={<Help />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="trips" element={<Trips />} />
              <Route path="trip-review" element={<RoleGate roles={HR_MGR}><TripReview /></RoleGate>} />
              <Route path="reimbursement-policies" element={<RoleGate roles={["super_admin","company_admin","hr","finance"]}><ReimbursementPolicies /></RoleGate>} />
              <Route path="learning" element={<Learning />} />
              <Route path="ats" element={<RoleGate roles={["super_admin","company_admin","hr","manager"]}><ATSPipeline /></RoleGate>} />
              <Route path="ai-studio" element={<RoleGate roles={["super_admin","company_admin","hr","manager"]}><AIGenerator /></RoleGate>} />
              <Route path="kpi-dashboard" element={<RoleGate roles={["super_admin","company_admin","hr","manager"]}><KPIDashboard /></RoleGate>} />
              <Route path="access" element={<RoleGate roles={["super_admin","company_admin","hr"]}><AccessControl /></RoleGate>} />
              <Route path="sales" element={<RoleGate roles={["super_admin"]}><SalesConsole /></RoleGate>} />
              <Route path="admin" element={<RoleGate roles={ADMIN}><TenantGuard><AdminPanel /></TenantGuard></RoleGate>} />
              <Route path="profile" element={<Profile />} />
              <Route path="tenants" element={<RoleGate roles={["super_admin"]}><AgencyTenants /></RoleGate>} />
              <Route path="billing" element={<RoleGate roles={["super_admin","company_admin"]}><Billing /></RoleGate>} />
              <Route path="billability" element={<RoleGate roles={HR_MGR}><Billability /></RoleGate>} />
              <Route path="invoicing-pipeline" element={<RoleGate roles={HR_MGR}><InvoicingPipeline /></RoleGate>} />
              <Route path="stat-compliance" element={<RoleGate roles={HR_MGR}><StatCompliance /></RoleGate>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}

export default App;
